import { usePublicMetadata } from '../seo/PublicMetadata.jsx';
import { useInitialPublicData } from '../seo/PublicBootstrap.jsx';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Clock, MapPin, Search, Store } from 'lucide-react';
import { Button, Card } from '@mts/ui';
import { usePublicTenant } from '../hooks/usePublicTenant';
import { fetchEventCategories, fetchEvents } from '../services/api';
import { buildPublicPageMetadata } from '../utils/publicMetadata.js';
import { buildBreadcrumbSchema, buildItemListSchema, buildWebPageSchema } from '../utils/structuredData.js';

function cleanString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function getSettingValue(tenant, key) {
  const list = Array.isArray(tenant?.tenantSettings) ? tenant.tenantSettings : [];
  return list.find((item) => item?.settingKey === key)?.settingValue;
}

function resolveMarketName({ tenant, spotlightName }) {
  return (
    cleanString(tenant?.city) ||
    cleanString(getSettingValue(tenant, 'tenant.city')) ||
    cleanString(spotlightName) ||
    'your community'
  );
}

function formatDate(value) {
  if (!value) return 'Date TBA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date TBA';
  return date.toLocaleDateString('en-US', { timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTimeRange(event) {
  const start = event?.startsAt ? new Date(event.startsAt) : null;
  const end = event?.endsAt ? new Date(event.endsAt) : null;
  if (!start || Number.isNaN(start.getTime())) return 'Time TBA';

  const startLabel = start.toLocaleTimeString('en-US', { timeZone: event.timezone || 'UTC', hour: 'numeric', minute: '2-digit' });
  if (!end || Number.isNaN(end.getTime())) return startLabel;
  const endLabel = end.toLocaleTimeString('en-US', { timeZone: event.timezone || 'UTC', hour: 'numeric', minute: '2-digit' });
  return `${startLabel} - ${endLabel}`;
}

function eventSearchText(event) {
  return [
    event?.title,
    event?.description,
    event?.location,
    event?.category?.name,
    event?.listing?.name,
  ].filter(Boolean).join(' ').toLowerCase();
}

function useEvents() {
  const initial = useInitialPublicData("events");
  const [events, setEvents] = useState(initial?.events || []);
  const [categories, setCategories] = useState(initial?.categories || []);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (initial && reloadKey === 0) { setEvents(initial.events); setCategories(initial.categories); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchEvents({ from: new Date().toISOString() }),
      fetchEventCategories(),
    ])
      .then(([eventRows, categoryRows]) => {
        if (cancelled) return;
        setEvents(Array.isArray(eventRows) ? eventRows : []);
        setCategories(Array.isArray(categoryRows) ? categoryRows : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setEvents([]);
        setCategories([]);
        setError(err?.message || 'Unable to load events right now.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey, initial]);

  return {
    events,
    categories,
    loading,
    error,
    retry: () => setReloadKey((value) => value + 1),
  };
}

function EventsHero({ marketName, searchQuery, onSearchChange, totalCount, resultCount }) {
  return (
    <section className="border-b border-border bg-surface">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
        <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr),360px] lg:items-end">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">Community Calendar</p>
            <h1 className="mt-3 max-w-4xl text-4xl font-bold leading-tight text-text-primary sm:text-5xl">
              Events in {marketName}
            </h1>
            <p className="mt-4 max-w-3xl text-lg leading-8 text-text-secondary">
              Find upcoming markets, workshops, live music, family activities, and local gatherings from the community.
            </p>
            <div className="mt-5 flex flex-wrap gap-3 text-sm text-text-secondary">
              <span className="inline-flex items-center gap-2 rounded-md border border-border bg-[var(--card,#FFFFFF)] px-3 py-2">
                <CalendarDays className="h-4 w-4 text-primary" aria-hidden="true" />
                {resultCount} of {totalCount} event{totalCount === 1 ? '' : 's'}
              </span>
            </div>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-text-primary">Search events</span>
            <span className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-secondary" aria-hidden="true" />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search by event or place"
                className="h-12 w-full rounded-lg border border-border bg-[var(--card,#FFFFFF)] pl-10 pr-4 text-sm text-text-primary outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
              />
            </span>
          </label>
        </div>
      </div>
    </section>
  );
}

function EventsFilterBar({ categories, activeCategory, onCategoryChange }) {
  return (
    <section className="sticky top-16 z-30 border-b border-border bg-[var(--card,#FFFFFF)]/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl gap-2 overflow-x-auto px-4 py-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => onCategoryChange('')}
          className={[
            'inline-flex h-9 shrink-0 items-center rounded-md border px-3 text-sm font-semibold transition',
            activeCategory === ''
              ? 'border-primary bg-primary text-white hover:text-[var(--primary-soft,#DBEAFE)]'
              : 'border-border bg-surface text-text-secondary hover:border-primary hover:text-primary',
          ].join(' ')}
        >
          All
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => onCategoryChange(category.id)}
            className={[
              'inline-flex h-9 shrink-0 items-center rounded-md border px-3 text-sm font-semibold transition',
              activeCategory === category.id
                ? 'border-primary bg-primary text-white hover:text-[var(--primary-soft,#DBEAFE)]'
                : 'border-border bg-surface text-text-secondary hover:border-primary hover:text-primary',
            ].join(' ')}
          >
            {category.name}
          </button>
        ))}
      </div>
    </section>
  );
}

function EventCard({ event }) {
  return (
    <Card padding="none" shadow="sm" className="h-full overflow-hidden border-border bg-[var(--card,#FFFFFF)]">
      {event.imageUrl ? <img src={event.imageUrl} alt="" className="aspect-video w-full object-cover" /> : null}
      <article className="flex h-full flex-col p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="rounded-lg border border-border bg-surface px-3 py-2 text-center">
            <div className="text-xs font-semibold uppercase tracking-wide text-primary">{formatDate(event.startsAt).split(' ')[0]}</div>
            <div className="mt-1 text-lg font-bold text-text-primary">{formatDate(event.startsAt).replace(/^[^ ]+ /, '')}</div>
          </div>
          {event.category?.name ? (
            <span className="shrink-0 rounded-md bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
              {event.category.name}
            </span>
          ) : null}
        </div>

        <h2 className="mt-4 text-xl font-bold leading-snug text-text-primary">
          <Link to={`/events/${encodeURIComponent(event.id)}`} className="hover:text-primary hover:underline">{event.title || 'Local event'}</Link>
        </h2>
        <p className="mt-3 line-clamp-3 flex-1 text-sm leading-6 text-text-secondary">
          {event.description ? event.description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : 'Event details are coming soon.'}
        </p>

        <div className="mt-5 space-y-2 border-t border-border pt-4 text-sm text-text-secondary">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
            <span>{formatTimeRange(event)}</span>
          </div>
          {event.location ? (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
              <span>{event.location}</span>
            </div>
          ) : null}
          {event.listing?.name ? (
            <div className="flex items-center gap-2">
              <Store className="h-4 w-4 text-primary" aria-hidden="true" />
              <span>{event.listing.name}</span>
            </div>
          ) : null}
        </div>
        <Link to={`/events/${encodeURIComponent(event.id)}`} className="mt-4 text-sm font-semibold text-primary hover:underline">View event details</Link>
      </article>
    </Card>
  );
}

function EventsSkeletonGrid() {
  return (
    <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" aria-label="Loading events">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="h-[310px] animate-pulse rounded-lg border border-border bg-[var(--card,#FFFFFF)] p-5">
          <div className="h-14 w-20 rounded bg-surface" />
          <div className="mt-5 h-6 w-3/4 rounded bg-surface" />
          <div className="mt-4 h-4 w-full rounded bg-surface" />
          <div className="mt-2 h-4 w-2/3 rounded bg-surface" />
          <div className="mt-8 h-4 w-1/2 rounded bg-surface" />
        </div>
      ))}
    </div>
  );
}

function EmptyEventsState({ hasFilters, onClear }) {
  return (
    <div className="rounded-lg border border-border bg-[var(--card,#FFFFFF)] p-8 text-center shadow-sm">
      <h2 className="text-2xl font-bold text-text-primary">No events found</h2>
      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-text-secondary">
        {hasFilters ? 'Try another search or category.' : 'Check back soon for new local events.'}
      </p>
      <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
        {hasFilters ? <Button type="button" variant="outline" onClick={onClear}>Clear Filters</Button> : null}
        <Button asChild variant="primary">
          <Link to="/listings">Browse Businesses</Link>
        </Button>
      </div>
    </div>
  );
}

function ErrorEventsState({ message, onRetry }) {
  return (
    <div className="rounded-lg border border-border bg-[var(--card,#FFFFFF)] p-8 text-center shadow-sm">
      <h2 className="text-2xl font-bold text-text-primary">Events could not load</h2>
      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-text-secondary">
        {message || 'Something went wrong while loading events.'}
      </p>
      <Button type="button" variant="primary" className="mt-6" onClick={onRetry}>Try Again</Button>
    </div>
  );
}

function EventsPage() {
  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const { events, categories, loading, error, retry } = useEvents();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const marketName = resolveMarketName({ tenant, spotlightName });

  usePublicMetadata(() => {
    if (!tenant) return;
    const title = `${marketName} Events & Community Calendar | ${spotlightName}`;
    const description = `Find upcoming events, activities, and community happenings in ${marketName}.`;
    const metadata = buildPublicPageMetadata({ tenant, pathname: '/events', canonicalPath: '/events', siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title, description, robots: searchQuery || activeCategory ? 'noindex,follow' : 'index,follow' });
    if (!searchQuery && !activeCategory) metadata.jsonLd = [buildWebPageSchema({ url: metadata.canonicalUrl, name: title, description, type: 'CollectionPage' }), buildItemListSchema({ url: metadata.canonicalUrl, name: title, items: events.map((event) => ({ name: event.title, url: `${tenant.canonicalOrigin}/events/${encodeURIComponent(event.id)}` })) }), buildBreadcrumbSchema({ url: metadata.canonicalUrl, items: [{ name: 'Home', url: tenant.canonicalOrigin }, { name: 'Events', url: metadata.canonicalUrl }] })];
    return (metadata);
  }, [activeCategory, events, marketName, searchQuery, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);

  const visibleEvents = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return events
      .filter((event) => !activeCategory || event.categoryId === activeCategory)
      .filter((event) => !query || eventSearchText(event).includes(query))
      .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  }, [activeCategory, events, searchQuery]);

  const hasFilters = Boolean(searchQuery.trim()) || Boolean(activeCategory);
  const clearFilters = () => {
    setSearchQuery('');
    setActiveCategory('');
  };

  return (
    <div className="bg-surface text-text-primary">
      <EventsHero
        marketName={marketName}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        totalCount={events.length}
        resultCount={visibleEvents.length}
      />

      <EventsFilterBar categories={categories} activeCategory={activeCategory} onCategoryChange={setActiveCategory} />

      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        {loading ? (
          <EventsSkeletonGrid />
        ) : error ? (
          <ErrorEventsState message={error} onRetry={retry} />
        ) : visibleEvents.length === 0 ? (
          <EmptyEventsState hasFilters={hasFilters} onClear={clearFilters} />
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {visibleEvents.map((event) => <EventCard key={event.id} event={event} />)}
          </div>
        )}
      </section>
    </div>
  );
}

export default EventsPage;
